import { useEffect, useState } from "react";
import { Flag, CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";

const TABS = ["PENDING", "APPROVED", "DENIED", "ALL"];
const STATUS_CLS = {
  PENDING: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  APPROVED: "bg-red-500/20 text-red-300 border-red-500/40",
  DENIED: "bg-slate-700 text-slate-300 border-slate-600",
};
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

function RequestActions({ row, onDone }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");
  const act = async (kind) => {
    if (kind === "approve" && !window.confirm(`Reject case "${row.lead_name}"? It moves to Rejected Leads (can be restored from Settings).`)) return;
    setBusy(kind);
    try {
      await api.post(`/admin/reject-requests/${row.id}/${kind}`, { admin_comment: comment });
      toast.success(kind === "approve" ? "Case rejected and moved to Rejected Leads" : "Request denied — case stays in bank pipeline");
      onDone();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(""); }
  };
  if (row.status !== "PENDING") {
    return (
      <div className="text-[11px] text-slate-400" data-testid={`rr-reviewed-${row.id}`}>
        {row.reviewed_by_name} · {fmt(row.reviewed_at)}
        {row.admin_comment && <div className="italic text-slate-500">"{row.admin_comment}"</div>}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 min-w-[190px]">
      <Input data-testid={`rr-comment-${row.id}`} value={comment} onChange={e => setComment(e.target.value)} placeholder="Admin comment (optional)"
        className="h-8 text-xs bg-slate-950 border-slate-700"/>
      <div className="flex gap-1.5">
        <button type="button" disabled={!!busy} onClick={() => act("approve")} data-testid={`rr-approve-${row.id}`}
          className="flex-1 h-8 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-50">
          {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin"/> : <CheckCircle2 className="w-3 h-3"/>} Approve Reject
        </button>
        <button type="button" disabled={!!busy} onClick={() => act("deny")} data-testid={`rr-deny-${row.id}`}
          className="flex-1 h-8 rounded-full bg-slate-700 hover:bg-slate-600 text-slate-100 text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-50">
          {busy === "deny" ? <Loader2 className="w-3 h-3 animate-spin"/> : <XCircle className="w-3 h-3"/>} Deny Request
        </button>
      </div>
    </div>
  );
}

function RequestRow({ r, onDone }) {
  return (
    <TableRow className="border-white/5 align-top" data-testid={`rr-row-${r.id}`}>
      <TableCell className="text-white">{r.lead_name}<div className="text-[10px] text-slate-500 font-mono-alt">{r.lead_id}</div></TableCell>
      <TableCell className="font-mono-alt">{r.lead_phone}</TableCell>
      <TableCell>{r.district}</TableCell>
      <TableCell>{r.loan_type}</TableCell>
      <TableCell className="text-white">{r.staff_name}<div className="text-[10px] text-slate-500">{r.staff_phone}</div></TableCell>
      <TableCell><Badge className="bg-red-500/15 text-red-300 border-red-500/40 text-[10px] whitespace-normal">{r.reason}</Badge></TableCell>
      <TableCell className="text-slate-300 text-xs whitespace-normal break-words max-w-[240px]">{r.detailed_reason}</TableCell>
      <TableCell className="text-xs whitespace-nowrap">{fmt(r.requested_at)}<div className="text-amber-300 font-bold">{r.waiting_days} day(s)</div></TableCell>
      <TableCell><Badge className={`${STATUS_CLS[r.status]} text-[10px]`}>{r.status}</Badge></TableCell>
      <TableCell><RequestActions row={r} onDone={onDone}/></TableCell>
    </TableRow>
  );
}

export default function RejectRequestsPage() {
  const [tab, setTab] = useState("PENDING");
  const [data, setData] = useState({ rows: [], stats: { pending: 0 } });
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/reject-requests", { params: { status: tab } })
      .then(r => setData(r.data)).catch(e => toast.error(errorMessage(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="card-surface p-6" data-testid="reject-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="rounded-xl border border-red-500/40 bg-[#1f0e0e] p-4 flex items-center gap-4" data-testid="rr-pending-card">
          <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center"><Flag className="w-5 h-5 text-red-400"/></div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Total Pending Requests</div>
            <div className="text-white font-heading text-2xl" data-testid="rr-pending-count">{data.stats.pending}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {TABS.map(t => (
            <button key={t} type="button" onClick={() => setTab(t)} data-testid={`rr-tab-${t.toLowerCase()}`}
              className={`px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-widest border ${tab === t ? "bg-amber-500 text-slate-900 border-amber-400" : "border-slate-700 text-slate-300 hover:bg-white/5"}`}>{t}</button>
          ))}
          <button type="button" onClick={load} data-testid="rr-refresh" className="w-8 h-8 rounded-full border border-slate-700 text-slate-300 flex items-center justify-center hover:bg-white/5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/>
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[1200px]">
          <Table className="text-xs [&_td]:px-2 [&_th]:px-2">
            <TableHeader>
              <TableRow className="border-white/5">
                {["Lead Name", "Phone", "District", "Loan Type", "Requested By", "Reason", "Detailed Note", "Requested On", "Status", "Actions"].map(h => <TableHead key={h}>{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map(r => <RequestRow key={r.id} r={r} onDone={load}/>)}
              {!loading && data.rows.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-10" data-testid="rr-empty">No {tab === "ALL" ? "" : tab.toLowerCase()} reject requests.</TableCell></TableRow>
              )}
              {loading && data.rows.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-10"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
